/**
 * Comprehensive error handling utilities for advisor invitations
 */

export interface AdvisorInvitationError {
  code: string;
  message: string;
  field?: string;
  details?: any;
}

export interface ErrorContext {
  operation: 'create' | 'verify' | 'accept' | 'reject' | 'list' | 'update';
  invitationId?: string;
  userAction?: string;
}

/**
 * Maps backend error codes to user-friendly messages
 */
const ERROR_MESSAGES: Record<string, string> = {
  // Validation errors
  VALIDATION_ERROR: 'Please check your input and try again',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_MOBILE: 'Please enter a valid mobile number with country code',
  MISSING_REQUIRED_FIELD: 'Please fill in all required fields',
  INVALID_ROLE: 'Please select a valid advisor role',
  NO_ENTITIES_SELECTED: 'Please select at least one investment entity',

  // Authentication errors
  AUTHENTICATION_REQUIRED: 'Please log in to perform this action',
  INSUFFICIENT_PERMISSIONS: 'You do not have permission to perform this action',
  INVALID_TOKEN: 'Your session has expired. Please log in again',

  // Invitation state errors
  INVITATION_NOT_FOUND: 'This invitation could not be found',
  INVITATION_EXPIRED: 'This invitation has expired. Please request a new one',
  INVITATION_ALREADY_ACCEPTED: 'This invitation has already been accepted',
  INVITATION_ALREADY_REJECTED: 'This invitation has already been declined',
  INVITATION_INVALID: 'This invitation is no longer valid',

  // Entity errors
  ENTITY_NOT_FOUND: 'The selected investment entity could not be found',
  ENTITY_ACCESS_DENIED: 'You do not have access to this investment entity',
  NO_ENTITIES_AVAILABLE: 'No investment entities are available for invitation',

  // Email errors
  EMAIL_SEND_FAILED: 'Failed to send invitation email. Please try again',
  EMAIL_INVALID_RECIPIENT: 'The email address is invalid or unreachable',

  // Rate limiting
  RATE_LIMIT_EXCEEDED: 'Too many requests. Please wait a moment and try again',

  // Network errors
  NETWORK_ERROR: 'Network connection error. Please check your internet connection',
  TIMEOUT_ERROR: 'Request timed out. Please try again',
  SERVER_ERROR: 'Server error occurred. Please try again later',

  // Generic fallbacks
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again',
  INTERNAL_ERROR: 'Internal server error. Please contact support if this persists',
};

/**
 * Maps specific field errors to user-friendly messages
 */
const FIELD_ERROR_MESSAGES: Record<string, string> = {
  firstName: 'First name is required',
  lastName: 'Last name is required',
  email: 'Please enter a valid email address',
  mobile: 'Please enter a valid mobile number with country code (e.g., +61412345678)',
  role: 'Please select an advisor role',
  investmentEntityIds: 'Please select at least one investment entity',
  reason: 'Please provide a reason for declining',
};

/**
 * Determines if an error is retryable
 */
export function isRetryableError(error: AdvisorInvitationError): boolean {
  const retryableCodes = [
    'NETWORK_ERROR',
    'TIMEOUT_ERROR',
    'SERVER_ERROR',
    'RATE_LIMIT_EXCEEDED',
    'EMAIL_SEND_FAILED',
  ];

  return retryableCodes.includes(error.code);
}

/**
 * Determines if an error requires user action (not retryable)
 */
export function requiresUserAction(error: AdvisorInvitationError): boolean {
  const userActionCodes = [
    'VALIDATION_ERROR',
    'INVALID_EMAIL',
    'INVALID_MOBILE',
    'MISSING_REQUIRED_FIELD',
    'INVALID_ROLE',
    'NO_ENTITIES_SELECTED',
    'AUTHENTICATION_REQUIRED',
    'INSUFFICIENT_PERMISSIONS',
    'INVITATION_EXPIRED',
    'INVITATION_ALREADY_ACCEPTED',
    'INVITATION_ALREADY_REJECTED',
    'INVITATION_INVALID',
    'ENTITY_NOT_FOUND',
    'ENTITY_ACCESS_DENIED',
    'NO_ENTITIES_AVAILABLE',
  ];

  return userActionCodes.includes(error.code);
}

/**
 * Gets user-friendly error message for display
 */
export function getErrorMessage(error: AdvisorInvitationError): string {
  // Try to get specific message first
  if (error.field && FIELD_ERROR_MESSAGES[error.field]) {
    return FIELD_ERROR_MESSAGES[error.field];
  }

  // Try to get general message
  if (ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }

  // Use provided message if available
  if (error.message) {
    return error.message;
  }

  // Fallback to generic message
  return ERROR_MESSAGES['UNKNOWN_ERROR'];
}

/**
 * Gets suggested action for error recovery
 */
export function getErrorAction(error: AdvisorInvitationError): string | null {
  switch (error.code) {
    case 'AUTHENTICATION_REQUIRED':
    case 'INVALID_TOKEN':
      return 'Please log in to continue';

    case 'INVITATION_EXPIRED':
      return 'Contact the person who sent the invitation for a new one';

    case 'INVITATION_ALREADY_ACCEPTED':
    case 'INVITATION_ALREADY_REJECTED':
      return 'This invitation has already been processed';

    case 'NO_ENTITIES_AVAILABLE':
      return 'Create investment entities before inviting advisors';

    case 'ENTITY_ACCESS_DENIED':
      return 'You do not have permission to invite advisors for this entity';

    case 'RATE_LIMIT_EXCEEDED':
      return 'Please wait a few minutes before trying again';

    case 'EMAIL_SEND_FAILED':
      return 'Check the email address and try again';

    case 'NETWORK_ERROR':
    case 'TIMEOUT_ERROR':
      return 'Check your internet connection and try again';

    default:
      return null;
  }
}

/**
 * Determines error severity for UI styling
 */
export function getErrorSeverity(error: AdvisorInvitationError): 'error' | 'warning' | 'info' {
  switch (error.code) {
    case 'INVITATION_EXPIRED':
    case 'INVITATION_ALREADY_ACCEPTED':
    case 'INVITATION_ALREADY_REJECTED':
      return 'warning';

    case 'RATE_LIMIT_EXCEEDED':
    case 'EMAIL_SEND_FAILED':
      return 'info';

    default:
      return 'error';
  }
}

/**
 * Parses backend error response into structured error
 */
export function parseBackendError(error: any): AdvisorInvitationError {
  // Handle structured backend errors
  if (error && typeof error === 'object') {
    if (error.code && error.error) {
      return {
        code: error.code,
        message: error.error,
        field: error.field,
        details: error.details,
      };
    }

    // Handle field-specific validation errors
    if (error.fieldErrors && Array.isArray(error.fieldErrors)) {
      const firstError = error.fieldErrors[0];
      return {
        code: 'VALIDATION_ERROR',
        message: firstError.message || 'Validation error',
        field: firstError.field,
        details: error.fieldErrors,
      };
    }

    // Handle generic error objects
    if (error.message) {
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message,
        details: error,
      };
    }
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      code: 'UNKNOWN_ERROR',
      message: error,
    };
  }

  // Handle Error instances
  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
      details: error,
    };
  }

  // Fallback for unknown error types
  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
    details: error,
  };
}

/**
 * Creates a standardized error handler for advisor invitation operations
 */
export function createErrorHandler(context: ErrorContext) {
  return (error: any): AdvisorInvitationError => {
    const parsedError = parseBackendError(error);

    // Add context-specific enhancements
    if (context.operation === 'verify' && parsedError.code === 'INVITATION_NOT_FOUND') {
      parsedError.message = 'This invitation link is invalid or has expired';
    }

    if (context.operation === 'accept' && parsedError.code === 'INVITATION_ALREADY_ACCEPTED') {
      parsedError.message =
        'This invitation has already been accepted. You may already have access to the system.';
    }

    if (context.operation === 'reject' && parsedError.code === 'INVITATION_ALREADY_REJECTED') {
      parsedError.message = 'This invitation has already been declined.';
    }

    return parsedError;
  };
}

/**
 * Logs error for debugging purposes
 */
export function logError(error: AdvisorInvitationError, context?: ErrorContext): void {
  const logData = {
    timestamp: new Date().toISOString(),
    error: {
      code: error.code,
      message: error.message,
      field: error.field,
    },
    context,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('Advisor Invitation Error:', logData);
  }

  // In production, you might want to send this to an error tracking service
  // Example: errorTrackingService.trackError(logData);
}

/**
 * Creates a comprehensive error handler that combines parsing, logging, and user-friendly messaging
 */
export function createComprehensiveErrorHandler(context: ErrorContext) {
  return (error: any) => {
    const parsedError = parseBackendError(error);
    logError(parsedError, context);

    return {
      error: parsedError,
      message: getErrorMessage(parsedError),
      action: getErrorAction(parsedError),
      severity: getErrorSeverity(parsedError),
      retryable: isRetryableError(parsedError),
      requiresUserAction: requiresUserAction(parsedError),
    };
  };
}
